<script setup lang="ts">
import { ref, h, computed } from 'vue'
import { usePersonnelStore } from '@/stores/personnel'
import { useAuthStore } from '@/stores/auth'
import { useOpsStore } from '@/stores/ops'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useRoute } from 'vue-router'
import type { ColumnDef } from '@tanstack/vue-table'
import type { Timestamp } from 'firebase/firestore'
import type { Personnel } from '@/types'
import FosTable from '@/components/ui/FosTable.vue'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import PersonnelRow from './PersonnelRow.vue'
import PersonnelDetailDialog from './PersonnelDetailDialog.vue'

const props = defineProps<{ searchQuery?: string }>()

const personnelStore = usePersonnelStore()
const authStore = useAuthStore()
const opsStore = useOpsStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()
const route = useRoute()
const incidentId = route.params.id as string

function canCheckOut(person: Personnel): boolean {
  if (person.time_out !== null) return false
  const isOwn = person.user_id === authStore.currentUser?.uid
  const canCheckOutOthers = permissionsStore.can('personnel.checkout_others', opRole.value)
  return isOwn || canCheckOutOthers
}

const selectedPerson = ref<Personnel | null>(null)
const showDetail = ref(false)
const pendingCheckout = ref<{ id: string; name: string } | null>(null)

function formatTime(ts: Timestamp | null): string {
  if (!ts) return '—'
  return ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function openDetail(person: Personnel) {
  selectedPerson.value = person
  showDetail.value = true
}

function requestCheckOut(personnelId: string, name: string) {
  pendingCheckout.value = { id: personnelId, name }
}

function confirmCheckOut() {
  if (!pendingCheckout.value) return
  personnelStore.checkOut(incidentId, opsStore.selectedOpId!, pendingCheckout.value.id)
  pendingCheckout.value = null
}

const filteredRoster = computed(() => {
  const q = props.searchQuery?.toLowerCase().trim()
  if (!q) return personnelStore.roster
  return personnelStore.roster.filter(
    (p) =>
      p.last?.toLowerCase().includes(q) ||
      p.first?.toLowerCase().includes(q) ||
      p.agency?.toLowerCase().includes(q),
  )
})

const columns: ColumnDef<Personnel>[] = [
  { accessorKey: 'last', header: 'Last Name', enableSorting: true },
  { accessorKey: 'first', header: 'First Name' },
  { accessorKey: 'agency', header: 'Agency' },
{
    id: 'status',
    header: 'Status',
    cell: ({ row }) => h(PersonnelRow, { status: row.original.status }),
  },
  {
    accessorKey: 'time_in',
    header: 'Time In',
    enableSorting: true,
    cell: ({ row }) => h('span', { class: 'font-mono' }, formatTime(row.original.time_in as Timestamp | null)),
  },
  {
    accessorKey: 'time_out',
    header: 'Time Out',
    cell: ({ row }) => h('span', { class: 'font-mono' }, formatTime(row.original.time_out as Timestamp | null)),
  },
  {
    id: 'actions',
    header: '',
    meta: { tdClass: 'w-px whitespace-nowrap' },
    cell: ({ row }) =>
      canCheckOut(row.original)
        ? h(
            FosButton,
            {
              variant: 'secondary',
              size: 'sm',
              onClick: (e: MouseEvent) => {
                e.stopPropagation()
                requestCheckOut(
                  row.original.personnel_id,
                  `${row.original.first} ${row.original.last}`,
                )
              },
            },
            () => 'Check Out',
          )
        : null,
  },
]
</script>

<template>
  <PersonnelDetailDialog v-model:visible="showDetail" :person="selectedPerson" />

  <FosAlertDialog
    :open="!!pendingCheckout"
    title="Confirm Check Out"
    :description="pendingCheckout ? `Check out ${pendingCheckout.name}?` : ''"
    confirm-label="Check Out"
    cancel-label="Cancel"
    @update:open="(v) => { if (!v) pendingCheckout = null }"
    @confirm="confirmCheckOut"
  />

  <FosTable :columns="columns" :data="filteredRoster" :page-size="50" :on-row-click="openDetail" />
</template>
